import type { Listing } from './layouts';

/**
 * The front door (E13): one question at the top of the Labs page, answered by picking the
 * question closest to your own. Each answer opens the tool that answers it.
 *
 * Pure. The page decides whether to show it; this decides what it offers.
 */
export type FrontDoorVariant = 'control' | 'question';
export type FrontDoorAnswer = 'learn_material' | 'check_standards' | 'share_materials' | 'find_job' | 'not_teaching' | 'other';

/** In the teacher's words, not the tool's. Order is the order on the page. */
export const FRONT_DOOR_QUESTIONS: readonly { answer: FrontDoorAnswer; tool: string; question: string }[] = [
  { answer: 'learn_material', tool: 'one_step_ahead', question: 'What do I need to know before I teach next week’s unit?' },
  { answer: 'check_standards', tool: 'objectives', question: 'Does this lesson actually hit the standards?' },
  { answer: 'share_materials', tool: 'collab', question: 'How do I get my team working on this together?' },
  { answer: 'find_job', tool: 'jobs', question: 'Where are the teaching jobs near me?' },
];

export interface FrontDoorChoice {
  answer: FrontDoorAnswer;
  tool: string;
  question: string;
  toolName: string;
  href: string;
}

/**
 * Only questions whose tool the page lists as open, so the front door can never offer a
 * tool its listing flag hides. A tool that opens later adds its question by being listed.
 */
export function frontDoorChoices(listings: readonly Listing[]): FrontDoorChoice[] {
  return FRONT_DOOR_QUESTIONS.flatMap(q => {
    const listing = listings.find(l => l.entry.id === q.tool && l.status === 'open');
    return listing?.href ? [{ ...q, toolName: listing.entry.name, href: listing.href }] : [];
  });
}

/** One question needs at least two answers to be a question. */
export const FRONT_DOOR_MIN_CHOICES = 2;

export const FRONT_DOOR_TEXT_LIMIT = 500;

/** A typed question, trimmed and capped, or null when there is nothing to send. */
export function cleanAsk(text: string): string | null {
  const cleaned = text.replace(/\s+/g, ' ').trim().slice(0, FRONT_DOOR_TEXT_LIMIT);
  return cleaned || null;
}
